import LockIcon from '@/icons/lock.svg';
import CheckIcon from '@/icons/check-icon.svg';
import {Skeleton} from '@features/ui/components/skeleton';
import {trpc} from '@features/utils/trpc';
import {type ChangeEventHandler, type ReactNode} from 'react';
import {useUpdateFlow} from '../flowHook';
import UserPlus from '@/icons/user_plus.svg';
import {Link} from '@tanstack/react-router';
import amplitudeService from '@features/analytics/amplitudeService';
import {getTeamDisplayName, TeamAvatar} from '@features/teams/user';

export const WorkspaceSelector = ({flowId}: {flowId: string}) => {
  const flow = trpc.flow.getFlow.useQuery({flowId});
  const teams = trpc.teams.getMyTeams.useQuery();
  const updateFlow = useUpdateFlow();

  // TODO: Handle error
  if (teams.isError || flow.isError) return null;

  if (teams.isPending || flow.isPending) {
    return (
      <>
        <WorkspaceOptionSkeleton />
        <WorkspaceOptionSkeleton />
      </>
    );
  }

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const {value} = event.target;
    const newTeamId = value === 'private' ? null : value;

    amplitudeService.logEvent('[Share] Workspace changed', {
      from: flow.data.teamId ?? 'private',
      to: newTeamId ?? 'private',
    });

    updateFlow.mutate({flowId, updates: {teamId: newTeamId}});
  };

  return (
    <div className='flex flex-col gap-2'>
      <WorkspaceOption
        checked={flow.data.teamId === null}
        displayName='Private'
        description='Visible to you and anyone with the link'
        icon={<LockIcon className='size-5' aria-hidden='true' />}
        value='private'
        onChange={onChange}
      />

      {teams.data.length === 0 && (
        <Link
          to='/settings'
          search={{tab: 'team'}}
          className='rounded-lg bg-muted p-3 has-[:checked]:bg-[color:color-mix(in_srgb,rgb(var(--primary))_20%,transparent)]'
          onClick={() => {
            amplitudeService.logEvent('[Share] Create team clicked', {
              source: 'workspace_selector',
            });
          }}
        >
          <div className='flex items-start gap-2'>
            <UserPlus />
            <div>
              <span className='font-semibold'>Create team</span>
              <div className='text-muted-foreground'>
                Set up a team for easy sharing
              </div>
            </div>
          </div>
        </Link>
      )}

      {teams.data.map((team) => {
        return (
          <WorkspaceOption
            checked={flow.data.teamId === team.id}
            value={team.id}
            displayName={getTeamDisplayName(team)}
            description='Visible to team and anyone with the link'
            icon={
              <TeamAvatar team={team} className='size-5' variant='no-ring' />
            }
            onChange={onChange}
            key={team.id}
          />
        );
      })}
    </div>
  );
};

const WorkspaceOption = ({
  value,
  displayName,
  checked,
  description,
  icon,
  onChange,
}: {
  value: string;
  checked: boolean;
  displayName: string;
  description: string;
  icon: ReactNode;
  onChange: ChangeEventHandler<HTMLInputElement>;
}) => (
  <label className='cursor-pointer rounded-lg bg-muted p-3 has-[:checked]:bg-[color:color-mix(in_srgb,rgb(var(--primary))_20%,transparent)]'>
    <div className='flex items-start justify-between gap-3'>
      <div className='flex items-start gap-1.5'>
        {icon}
        <div className='flex flex-col gap-0.5'>
          <span className='font-semibold'>{displayName}</span>
          <span className='text-muted-foreground'>{description}</span>
        </div>
      </div>
      {checked && <CheckIcon className='h-4 w-4 flex-shrink-0' />}
    </div>
    <input
      className='hidden'
      type='radio'
      name='workspace'
      value={value}
      checked={checked}
      onChange={onChange}
    />
  </label>
);

const WorkspaceOptionSkeleton = () => (
  <div className='rounded-lg border border-transparent p-2'>
    <div className='flex items-start gap-2'>
      <Skeleton className='size-6 rounded-full' />
      <div className='flex flex-1 flex-col gap-2'>
        <Skeleton className='h-4 w-24' />
        <Skeleton className='h-3 w-40 max-w-full' />
      </div>
    </div>
  </div>
);
